// Self-contained printable receiver report opened in a new tab from the
// Agent Portal's ReceiverDetailDialog. Mirrors the style of
// agentSelfPrintableReport.js but scoped to a single receiver.

const fmtUSD = (n) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", maximumFractionDigits: 2,
}).format(Number(n || 0));

const safe = (s) => (s == null ? "—" : String(s));

const escapeHtml = (s) => safe(s)
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

/**
 * @param {object} agent     /agent/me payload (full_name, agent_code)
 * @param {object} payload   /agent/receiver response { profile, cards, commissions, totals }
 */
export function buildReceiverPrintableReport(agent, payload) {
  const p = payload?.profile || {};
  const totals = payload?.totals || {};
  const commissions = Array.isArray(payload?.commissions) ? payload.commissions : [];
  const generatedAt = new Date().toLocaleString("en-US", {
    dateStyle: "long", timeStyle: "short",
  });

  // Hand-off receipt: show ONLY what the receiver needs to see — the
  // transaction list (amount, date/time, type, cash or card). Card numbers,
  // commission earnings and other internal agent data are intentionally
  // omitted.
  const TYPE_LABEL = {
    issuance: "Card Issuance",
    topup: "Top-up",
    cash_deposit: "Cash Deposit",
  };
  const METHOD_LABEL = { cash: "Cash", card: "Card" };
  const METHOD_CLASS = { cash: "method-cash", card: "method-card" };

  const sorted = [...commissions].sort((a, b) => {
    const ta = new Date(a?.earned_at || 0).getTime();
    const tb = new Date(b?.earned_at || 0).getTime();
    return tb - ta;
  });

  const totalFunded = sorted.reduce(
    (acc, r) => acc + Number(r?.transaction_amount || 0),
    0,
  );

  const txRows = sorted.length === 0
    ? `<tr><td colspan="4" class="small" style="text-align:center; padding:18px;">No transactions recorded for this receiver.</td></tr>`
    : sorted.map((r) => {
        const d = r?.earned_at ? new Date(r.earned_at) : null;
        const dateStr = d ? d.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—";
        const type = TYPE_LABEL[r?.transaction_type] || (r?.transaction_type || "—").replace(/_/g, " ");
        const method = (r?.payment_method || "cash").toLowerCase();
        const methodLabel = METHOD_LABEL[method] || "Cash";
        const methodCls = METHOD_CLASS[method] || "method-cash";
        return `<tr>
            <td class="small">${escapeHtml(dateStr)}</td>
            <td>${escapeHtml(type)}</td>
            <td class="num strong">${escapeHtml(fmtUSD(r?.transaction_amount))}</td>
            <td><span class="method-pill ${methodCls}">${escapeHtml(methodLabel)}</span></td>
        </tr>`;
      }).join("");

  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Zcardon · Receiver ${escapeHtml(p.receiver_name || p.receiver_email)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif; color: #112318; background: #fff; margin: 0; padding: 32px 40px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .doc { max-width: 1000px; margin: 0 auto; }
  .brand { font-size: 11px; letter-spacing: 0.3em; text-transform: uppercase; color: #4A5C50; }
  h1 { font-family: "Cormorant Garamond", "Times New Roman", Georgia, serif; font-weight: 500; font-size: 38px; margin: 4px 0 0; letter-spacing: -0.5px; }
  h2 { font-family: "Cormorant Garamond", Georgia, serif; font-weight: 500; font-size: 22px; margin: 28px 0 8px; border-bottom: 1px solid #E5E0D8; padding-bottom: 4px; }
  .sub { color: #4A5C50; font-size: 13px; margin-top: 6px; }
  .meta { margin-top: 14px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
  .meta .item { border: 1px solid #E5E0D8; padding: 10px 12px; border-radius: 8px; }
  .meta .label { font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; color: #4A5C50; }
  .meta .val { font-size: 14px; margin-top: 3px; font-family: "SF Mono", Menlo, monospace; }
  .stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin-top: 14px; }
  .stat { border: 1px solid #E5E0D8; border-radius: 10px; padding: 12px; text-align: center; }
  .stat .label { font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; color: #4A5C50; }
  .stat .val { font-family: "Cormorant Garamond", Georgia, serif; font-size: 22px; margin-top: 6px; }
  .stat.commission .val { color: #184A30; }
  .stat.balance .val { color: #0F3320; }
  table { width: 100%; border-collapse: collapse; margin-top: 6px; font-size: 12px; }
  th, td { padding: 7px 8px; border-bottom: 1px solid #E5E0D8; text-align: left; vertical-align: top; }
  th { font-size: 10px; letter-spacing: 0.15em; text-transform: uppercase; color: #4A5C50; }
  td.num { text-align: right; font-variant-numeric: tabular-nums; font-family: "SF Mono", Menlo, monospace; }
  td.strong, .strong { font-weight: 600; }
  td.small, .small { font-size: 11px; color: #4A5C50; }
  .mono { font-family: "SF Mono", Menlo, monospace; }
  .badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; font-weight: 600; }
  .badge.active  { background: #DCF7E8; color: #166434; }
  .badge.pending { background: #FEF3C7; color: #92400E; }
  .badge.muted   { background: #F1ECE3; color: #4A5C50; }
  .method-pill { display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; font-weight: 700; border: 1px solid transparent; }
  .method-cash { background: #FEF3C7; color: #78350F; border-color: #FDE68A; }
  .method-card { background: #DBEAFE; color: #1E3A8A; border-color: #BFDBFE; }
  .tx-total-row td { background: #FBF7EE; font-weight: 600; border-top: 2px solid #112318; }
  .footer { margin-top: 36px; font-size: 10px; color: #4A5C50; text-align: center; }
  .actions { position: fixed; top: 12px; right: 12px; }
  .actions button { padding: 6px 14px; border-radius: 999px; border: 1px solid #112318; background: #112318; color: #fff; font-size: 12px; cursor: pointer; margin-left: 6px; }
  .actions button.secondary { background: #fff; color: #112318; }
  @media print { .actions { display: none; } body { padding: 0 16px; } }
</style>
</head>
<body>
  <div class="actions">
    <button onclick="window.print()">Print</button>
    <button class="secondary" onclick="window.close()">Close</button>
  </div>
  <div class="doc">
    <div class="brand">Zcardon · Receiver profile</div>
    <h1>${escapeHtml(p.receiver_name || p.receiver_email)}</h1>
    <div class="sub">Issued by agent <span class="strong">${escapeHtml(agent?.full_name)}</span> · code <span class="mono strong">${escapeHtml(agent?.agent_code)}</span></div>
    <div class="sub">Generated ${escapeHtml(generatedAt)}</div>

    <div class="meta">
      <div class="item"><div class="label">Email</div><div class="val">${escapeHtml(p.receiver_email)}</div></div>
      <div class="item"><div class="label">Phone</div><div class="val">${escapeHtml(p.receiver_phone || "—")}</div></div>
      <div class="item"><div class="label">First seen</div><div class="val">${escapeHtml(totals.first_seen ? new Date(totals.first_seen).toLocaleDateString() : "—")}</div></div>
    </div>

    <h2>Transactions</h2>
    <table data-testid="receiver-print-tx-table">
      <thead>
        <tr>
          <th style="width: 30%;">Date &amp; time</th>
          <th style="width: 32%;">Type</th>
          <th style="width: 20%; text-align: right;">Amount</th>
          <th style="width: 18%;">Payment</th>
        </tr>
      </thead>
      <tbody>
        ${txRows}
        ${sorted.length > 0 ? `<tr class="tx-total-row">
          <td colspan="2" class="strong">Total funded</td>
          <td class="num strong">${escapeHtml(fmtUSD(totalFunded))}</td>
          <td></td>
        </tr>` : ""}
      </tbody>
    </table>

    <div class="footer">Zcardon · Confidential · Do not share outside the Zcardon platform.</div>
  </div>
  <script>
    if (!window.matchMedia('print').matches) {
      setTimeout(function () { window.print(); }, 250);
    }
  </script>
</body>
</html>`;

  // Print via a Blob URL. This is more reliable than `window.open("", "_blank")`
  // (which was returning null in modern browsers when the features string
  // included `noopener,noreferrer`) AND it avoids the ~2MB data-URL size cap
  // that bites on long commission tables.
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank");
  if (w) {
    // Revoke the URL after the new tab has had a chance to load it.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return { ok: true };
  }
  // Popup truly blocked — emit a clickable fallback via an anchor element
  // so the user can still get to the print view.
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 60_000);
  return { ok: true, fallback: true };
}
